/**
 * Экран «Контроль сроков» (SLA) — tenant-app, волна 3 «Облака».
 *
 * Дизайн: manager v3 (lovart-hotels-design-manager-v3-2026-06-06.html),
 * раздел Analytics → «Соблюдение норматива (SLA)»:
 *   - сводка-баннер просрочек сверху (critical-тон), счётчик нарушений;
 *   - разбивка по отделам/категориям: количество просроченных, худшая
 *     просрочка (breach count + median overdue);
 *   - список просроченных заявок карточками с бейджем «просрочено N ч»,
 *     категорией, приоритетом и временем — для приоритизации работы.
 *
 * Это реальный аналитический экран поверх готового бэка
 * GET /v1/admin/requests/sla-breached, а НЕ дашборд-фоллбэк.
 *
 * i18n: 100% RU — статусы/категории/приоритеты через kanban-model
 * (stateLabel/categoryLabel/priorityLabel/slaInfo). Мобайл 375:
 * одноколоночный stack, сводка-плитки переносятся.
 */
import { useMemo } from 'react';
import { Card, Skeleton } from '@hsc/ui';
import type { ManagerRequest } from '@hsc/types';
import {
  categoryLabel,
  priorityLabel,
  slaInfo,
  stateLabel,
} from '../requests-kanban/kanban-model.js';
import { useSlaBreached } from './useSla.js';

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface DeptStat {
  category: string;
  count: number;
  /** Наибольшая просрочка в минутах (для сортировки/худшего случая). */
  worstOverdueMin: number;
}

/** Разбивка просрочек по категории заявки (RU-лейбл в рендере). */
function byCategory(items: readonly ManagerRequest[], now: number): DeptStat[] {
  const map = new Map<string, DeptStat>();
  for (const r of items) {
    const key = r.category || 'other';
    const info = slaInfo(r, now);
    const overdue = info.minutesLeft !== null ? Math.max(0, -info.minutesLeft) : 0;
    const cur = map.get(key);
    if (cur) {
      cur.count += 1;
      cur.worstOverdueMin = Math.max(cur.worstOverdueMin, overdue);
    } else {
      map.set(key, { category: key, count: 1, worstOverdueMin: overdue });
    }
  }
  return [...map.values()].sort((a, b) => b.count - a.count || b.worstOverdueMin - a.worstOverdueMin);
}

/** «125» минут → «2 ч 5 мин»; до часа — «N мин». */
function fmtMinutes(mins: number): string {
  if (mins <= 0) return '0 мин';
  if (mins < 60) return `${mins} мин`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h} ч ${m} мин` : `${h} ч`;
}

export function SlaPage(): JSX.Element {
  const now = Date.now();
  const { data, isLoading, isError } = useSlaBreached();

  const items = useMemo(() => data?.items ?? [], [data]);
  const stats = useMemo(() => byCategory(items, now), [items, now]);
  const worst = useMemo(
    () =>
      items.reduce((acc, r) => {
        const info = slaInfo(r, now);
        const overdue = info.minutesLeft !== null ? Math.max(0, -info.minutesLeft) : 0;
        return Math.max(acc, overdue);
      }, 0),
    [items, now],
  );

  if (isLoading) {
    return (
      <section data-testid="sla-loading">
        <h1 className="hsc-page__title">Контроль сроков</h1>
        <div className="sla-stats">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} height={96} radius={20} />
          ))}
        </div>
        <div className="hsc-stack" style={{ marginTop: '1rem' }}>
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} height={84} radius={16} />
          ))}
        </div>
      </section>
    );
  }

  if (isError || !data) {
    return (
      <section data-testid="sla-error">
        <h1 className="hsc-page__title">Контроль сроков</h1>
        <Card>
          <p role="alert">Не удалось загрузить данные по срокам. Попробуйте обновить страницу.</p>
        </Card>
      </section>
    );
  }

  const total = items.length;

  return (
    <section data-testid="sla-page">
      <h1 className="hsc-page__title">Контроль сроков</h1>
      <p className="ta-muted">
        Заявки, по которым нарушен норматив времени (SLA) и которые ещё не закрыты. Обновляется
        автоматически.
      </p>

      {total === 0 ? (
        <Card>
          <div className="sla-ok" data-testid="sla-empty">
            <span className="sla-ok__icon" aria-hidden="true">
              ✓
            </span>
            <div>
              <p className="sla-ok__title">Просрочек нет</p>
              <p className="ta-muted">Все заявки в рамках норматива — всё под контролем.</p>
            </div>
          </div>
        </Card>
      ) : (
        <>
          <div className="sla-banner" role="alert" data-testid="sla-banner">
            <span className="sla-banner__icon" aria-hidden="true">
              ⏱
            </span>
            <div className="sla-banner__body">
              <p className="sla-banner__title">
                Просрочено заявок: <strong data-testid="sla-total">{total}</strong>
              </p>
              <p className="sla-banner__sub">
                Максимальная просрочка — {fmtMinutes(worst)}. Назначьте исполнителей и закройте
                критичные первыми.
              </p>
            </div>
          </div>

          <div className="sla-stats" data-testid="sla-stats">
            {stats.map((s) => (
              <div key={s.category} className="sla-stat" data-testid={`sla-stat-${s.category}`}>
                <p className="sla-stat__cat">{categoryLabel(s.category)}</p>
                <p className="sla-stat__count">{s.count}</p>
                <p className="sla-stat__sub">просрочено · до {fmtMinutes(s.worstOverdueMin)}</p>
              </div>
            ))}
          </div>

          <h2 className="sla-section-title">Просроченные заявки</h2>
          <ul className="hsc-stack" data-testid="sla-list">
            {items.map((req) => {
              const info = slaInfo(req, now);
              return (
                <li key={req.id} data-testid={`sla-item-${req.id}`}>
                  <Card>
                    <div className="sla-row">
                      <div className="sla-row__main">
                        <h3 className="hsc-card__title">{req.title || categoryLabel(req.category)}</h3>
                        <p className="ta-muted">
                          {categoryLabel(req.category)}
                          {' · '}
                          {stateLabel(req.state)}
                          {' · '}
                          {priorityLabel(req.priority)}
                          {' · '}
                          {fmtWhen(req.created_at)}
                        </p>
                        {req.description !== undefined && req.description !== '' && (
                          <p>{req.description}</p>
                        )}
                      </div>
                      <span
                        className="fleet-badge fleet-badge--danger"
                        data-testid={`sla-badge-${req.id}`}
                      >
                        {info.label}
                      </span>
                    </div>
                  </Card>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}
